import { RouteProp, useFocusEffect, useNavigation, useRoute } from "@react-navigation/native";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { Pressable, StyleSheet, View } from "react-native";
import {
  useGetAiTutorQuery,
  useGetAiTutorLtiAnalyticsQuery,
  useGetTenantForSchoolAdminQuery,
} from "@axio/graphql/generated/graphql";
import CustomText from "../components/common/general/CustomText/CustomText";
import CustomSpacing from "../components/common/layout/CustomSpacing/CustomSpacing";
import LTILayout from "../layouts/LTILayout";
import type { RootStackParamList } from "../navigation/navigation.types";
import { Feather, Ionicons } from "@expo/vector-icons";
import { formatDurationFromSeconds } from "../utils/timeUtils";
import { useAuth } from "../hooks/useAuth";
import Sentry from "../utils/sentry";
import { UserRoleUI, hasMinRole } from "../constants/rolesAndPermissions";
import {
  buildOverviewSampleDetail,
  formatObjectiveTime,
  formatSentimentLabel,
  formatStatusLabel,
  formatTime,
  getInitials,
  mergeObjectiveStatus,
} from "./LTIAnalyticsView.utils";

type LtiAnalyticsTab = "overview" | "students";

type LtiAnalyticsQueryData = ReturnType<typeof useGetAiTutorLtiAnalyticsQuery>["data"];

type LtiAnalyticsRecord = NonNullable<
  NonNullable<NonNullable<NonNullable<LtiAnalyticsQueryData>["getAITutorLtiAnalytics"]>["data"]>[number]
>;

type LtiAnalyticsStudent = LtiAnalyticsRecord["students"][number];
type LtiAnalyticsRecentActivity = LtiAnalyticsRecord["recent_activity"][number];

type StudentAnalyticsRowProps = {
  student: LtiAnalyticsStudent;
  analytics?: LtiAnalyticsRecord | null;
  isExpanded: boolean;
  studentObjectives: LtiAnalyticsRecentActivity[];
  expandedStudentObjectiveKey: string | null;
  setExpandedStudentId: (studentId: string | null) => void;
  setExpandedStudentObjectiveKey: (objectiveKey: string | null) => void;
  t: ReturnType<typeof useTranslation>["t"];
};

type OverviewObjectiveCardProps = {
  activity: LtiAnalyticsRecentActivity;
  idx: number;
  isLast: boolean;
  isExpanded: boolean;
  analytics?: LtiAnalyticsRecord | null;
  setExpandedOverviewObjectiveId: (objectiveId: string | null) => void;
  t: ReturnType<typeof useTranslation>["t"];
};

const OverviewObjectiveCard = ({
  activity,
  idx,
  isLast,
  isExpanded,
  analytics,
  setExpandedOverviewObjectiveId,
  t,
}: OverviewObjectiveCardProps) => {
  const objectiveKey = activity.objective_id || `${activity.objective_label}-${idx}`;
  const objectiveSample = buildOverviewSampleDetail(activity.objective_label, {
    summaries: analytics?.summaries,
    success_points_details: analytics?.success_points_details,
    confusion_points_details: analytics?.confusion_points_details,
    further_recommendations: analytics?.further_recommendations,
  });

  return (
    <View style={[styles.overviewObjectiveCard, isLast && styles.overviewObjectiveCardLast]}>
      <Pressable
        onPress={() => setExpandedOverviewObjectiveId(isExpanded ? null : objectiveKey)}
        accessibilityRole="button"
        accessibilityLabel={
          isExpanded
            ? t("Collapse objective: {{label}}", { label: activity.objective_label })
            : t("Expand objective: {{label}}", { label: activity.objective_label })
        }
        accessibilityState={{ expanded: isExpanded }}
        style={({ pressed }) => [styles.overviewObjectiveButton, pressed && styles.overviewObjectiveButtonPressed]}>
        <View style={styles.overviewObjectiveHeaderRow}>
          <View style={styles.overviewObjectiveHeaderContent}>
            <CustomText
              text={`${idx + 1}. ${activity.objective_label}`}
              size="s"
              useTranslationText={false}
              style={styles.overviewObjectiveLabel}
            />
            <View style={styles.overviewObjectiveMetaRow}>
              <View style={styles.metaItem}>
                <Feather name="check-circle" size={12} color="#6B7280" />
                <CustomText
                  text={`${activity.success_points_count} success`}
                  size="xs"
                  useTranslationText={false}
                  style={styles.metaText}
                />
              </View>
              {activity.confusion_points_count > 0 && (
                <View style={styles.metaItem}>
                  <Feather name="alert-circle" size={12} color="#6B7280" />
                  <CustomText
                    text={`${activity.confusion_points_count} confusion`}
                    size="xs"
                    useTranslationText={false}
                    style={styles.metaText}
                  />
                </View>
              )}
              {activity.questions_asked > 0 && (
                <View style={styles.metaItem}>
                  <Feather name="message-square" size={12} color="#6B7280" />
                  <CustomText
                    text={`${activity.questions_asked} qs`}
                    size="xs"
                    useTranslationText={false}
                    style={styles.metaText}
                  />
                </View>
              )}
              <View style={styles.metaItem}>
                <Feather name="clock" size={12} color="#6B7280" />
                <CustomText
                  text={formatObjectiveTime(activity.total_time_taken)}
                  size="xs"
                  useTranslationText={false}
                  style={styles.metaText}
                />
              </View>
            </View>
          </View>
          <Feather name={isExpanded ? "chevron-up" : "chevron-down"} size={14} color="#9CA3AF" />
        </View>
      </Pressable>

      {isExpanded && (
        <View style={styles.overviewObjectiveExpandedBody}>
          <View style={styles.overviewSummaryBox}>
            <View style={styles.overviewSummaryRow}>
              <Ionicons name="sparkles-outline" size={14} color="#7C3AED" />
              <CustomText
                text={objectiveSample.summary}
                size="xs"
                useTranslationText={false}
                style={styles.overviewSummaryText}
              />
            </View>
          </View>

          <View style={styles.overviewSuccessBox}>
            <View style={styles.overviewSectionHeadingRow}>
              <Feather name="check-circle" size={14} color="#059669" />
              <CustomText
                text={t("What Went Well")}
                size="s"
                weight="bold"
                useTranslationText={false}
                style={styles.overviewSectionHeadingText}
              />
            </View>
            <View style={styles.overviewBulletList}>
              {objectiveSample.successPoints.map((point, pointIdx) => (
                <View key={pointIdx} style={styles.overviewBulletItem}>
                  <CustomText text="•" size="xs" useTranslationText={false} style={styles.overviewBullet} />
                  <CustomText text={point} size="xs" useTranslationText={false} style={styles.overviewBulletText} />
                </View>
              ))}
            </View>
          </View>

          <View style={styles.overviewConfusionBox}>
            <View style={styles.overviewSectionHeadingRow}>
              <Feather name="alert-circle" size={14} color="#DC2626" />
              <CustomText
                text={t("Areas of Confusion")}
                size="s"
                weight="bold"
                useTranslationText={false}
                style={styles.overviewSectionHeadingText}
              />
            </View>
            <View style={styles.overviewBulletList}>
              {objectiveSample.confusionPoints.map((point, pointIdx) => (
                <View key={pointIdx} style={styles.overviewBulletItem}>
                  <CustomText text="•" size="xs" useTranslationText={false} style={styles.overviewBullet} />
                  <CustomText text={point} size="xs" useTranslationText={false} style={styles.overviewBulletText} />
                </View>
              ))}
            </View>
          </View>
        </View>
      )}
    </View>
  );
};

const StudentAnalyticsRow = ({
  student,
  analytics,
  isExpanded,
  studentObjectives,
  expandedStudentObjectiveKey,
  setExpandedStudentId,
  setExpandedStudentObjectiveKey,
  t,
}: StudentAnalyticsRowProps) => {
  const statusLabel = formatStatusLabel(student.status);
  const sentimentLabel = formatSentimentLabel(student.sentiment);
  const isComplete = student.status === "complete";
  const isNotStarted = student.status === "not_started";
  const isPositive = student.sentiment === "positive";
  const isNegative = student.sentiment === "negative";
  const studentMessagesCount = studentObjectives.reduce(
    (sum, objective) => sum + (objective.student_messages_count ?? 0),
    0
  );
  const studentQuestionsAskedCount = studentObjectives.reduce(
    (sum, objective) => sum + (objective.questions_asked ?? 0),
    0
  );
  const studentSuccessPointsCount = studentObjectives.reduce(
    (sum, objective) => sum + (objective.success_points_count ?? 0),
    0
  );
  const studentAiMessagesCount = studentObjectives.reduce(
    (sum, objective) => sum + (objective.ai_messages_count ?? 0),
    0
  );
  const studentSummary = analytics?.summaries?.[0] || student.originality_analysis || "";
  const studentRecommendations = analytics?.further_recommendations || [];

  return (
    <View>
      <Pressable
        onPress={() => setExpandedStudentId(isExpanded ? null : student.student_id)}
        style={styles.studentsBodyRow}>
        <View style={[styles.studentIdentityCell, styles.studentColumn]}>
          <View style={styles.studentAvatar}>
            <CustomText
              text={getInitials(student.student_name)}
              size="xs"
              weight="bold"
              useTranslationText={false}
              style={styles.studentAvatarText}
            />
          </View>
          <View style={styles.studentNameBlock}>
            <CustomText
              text={student.student_name}
              size="s"
              useTranslationText={false}
              style={styles.studentNameText}
              numberOfLines={1}
            />
            <CustomText
              text={student.student_email || "-"}
              size="xs"
              useTranslationText={false}
              style={styles.studentEmailText}
              numberOfLines={1}
            />
          </View>
        </View>
        <View style={styles.statusColumn}>
          <View
            style={[
              styles.statusPill,
              isComplete
                ? styles.statusPillComplete
                : isNotStarted
                  ? styles.statusPillNotStarted
                  : styles.statusPillProgress,
            ]}>
            <CustomText
              text={statusLabel}
              size="xs"
              useTranslationText={false}
              style={
                isComplete
                  ? styles.statusPillCompleteText
                  : isNotStarted
                    ? styles.statusPillNotStartedText
                    : styles.statusPillProgressText
              }
            />
          </View>
        </View>
        <CustomText
          text={formatTime(student.time_spent)}
          size="s"
          useTranslationText={false}
          style={[styles.studentsCellText, styles.timeColumn]}
        />
        <CustomText
          text={String(student.confusion_points_count)}
          size="s"
          useTranslationText={false}
          style={[styles.studentsCellText, styles.confusionColumn]}
        />
        <View style={styles.sentimentColumn}>
          <View
            style={[
              styles.sentimentPill,
              isPositive
                ? styles.sentimentPillPositive
                : isNegative
                  ? styles.sentimentPillNegative
                  : styles.sentimentPillNeutral,
            ]}>
            <CustomText
              text={sentimentLabel}
              size="xs"
              useTranslationText={false}
              style={
                isPositive
                  ? styles.sentimentPillPositiveText
                  : isNegative
                    ? styles.sentimentPillNegativeText
                    : styles.sentimentPillNeutralText
              }
            />
          </View>
        </View>
        <View style={styles.originalityColumn}>
          <CustomText
            text={student.originality_analysis || "-"}
            size="xs"
            useTranslationText={false}
            style={styles.originalityText}
            numberOfLines={2}
            ellipsizeMode="tail"
          />
        </View>
        <View style={[styles.studentsChevronColumn, isExpanded && styles.chevronRotated]}>
          <Feather name="chevron-down" size={16} color="#0F172A" />
        </View>
      </Pressable>

      {isExpanded && (
        <View style={styles.expandedRowDetail}>
          <View style={styles.expandedMetricsRow}>
            <View style={styles.metricBadge}>
              <Feather name="check-circle" size={12} color="#059669" />
              <CustomText
                text={` ${t("Success points")} : ${student?.success_points_count ?? 0}`}
                size="xs"
                useTranslationText={false}
                style={styles.expandedMetricText}
              />
            </View>
            <View style={styles.metricBadge}>
              <Feather name="alert-circle" size={12} color="#DC2626" />
              <CustomText
                text={` ${t("Confusion points")} : ${student?.confusion_points_count ?? 0}`}
                size="xs"
                useTranslationText={false}
                style={styles.expandedMetricText}
              />
            </View>
            <View style={styles.metricBadge}>
              <Feather name="message-square" size={12} color="#6B7280" />
              <CustomText
                text={`${studentQuestionsAskedCount} questions`}
                size="xs"
                useTranslationText={false}
                style={styles.expandedMetricText}
              />
            </View>
          </View>

          <View style={styles.expandedTimingStrip}>
            <View style={styles.expandedTimingItem}>
              <CustomText
                text={t("Time Spent")}
                size="xs"
                useTranslationText={false}
                style={styles.expandedTimingLabel}
              />
              <CustomText
                text={formatTime(student.time_spent)}
                size="s"
                weight="bold"
                useTranslationText={false}
                style={styles.expandedTimingValue}
              />
            </View>
            <View style={styles.expandedTimingDivider} />
            <View style={styles.expandedTimingItem}>
              <CustomText
                text={t("Student Msgs")}
                size="xs"
                useTranslationText={false}
                style={styles.expandedTimingLabel}
              />
              <CustomText
                text={String(studentMessagesCount)}
                size="s"
                weight="bold"
                useTranslationText={false}
                style={styles.expandedTimingValue}
              />
            </View>
            <View style={styles.expandedTimingDivider} />
            <View style={styles.expandedTimingItem}>
              <CustomText
                text={t("Student Response")}
                size="xs"
                useTranslationText={false}
                style={styles.expandedTimingLabel}
              />
              <CustomText
                text={(() => {
                  const val = studentObjectives.find(
                    (a) => a.student_id === student.student_id
                  )?.average_student_response_time;
                  return val != null ? formatDurationFromSeconds(val) : "—";
                })()}
                size="s"
                weight="bold"
                useTranslationText={false}
                style={styles.expandedTimingValue}
              />
            </View>
            <View style={styles.expandedTimingDivider} />
            <View style={styles.expandedTimingItem}>
              <CustomText text={t("AI Msgs")} size="xs" useTranslationText={false} style={styles.expandedTimingLabel} />
              <CustomText
                text={String(studentAiMessagesCount)}
                size="s"
                weight="bold"
                useTranslationText={false}
                style={styles.expandedTimingValue}
              />
            </View>
            <View style={styles.expandedTimingDivider} />
            <View style={styles.expandedTimingItem}>
              <CustomText
                text={t("AI Response")}
                size="xs"
                useTranslationText={false}
                style={styles.expandedTimingLabel}
              />
              <CustomText
                text={
                  student.average_ai_response_time != null
                    ? formatDurationFromSeconds(student.average_ai_response_time)
                    : "—"
                }
                size="s"
                weight="bold"
                useTranslationText={false}
                style={styles.expandedTimingValue}
              />
            </View>
          </View>

          {(studentSummary || studentRecommendations.length > 0) && (
            <View style={styles.studentRecommendationCard}>
              {studentSummary ? (
                <View style={styles.studentRecommendationHeaderRow}>
                  <Ionicons name="sparkles-outline" size={14} color="#7C3AED" />
                  <CustomText
                    text={studentSummary}
                    size="xs"
                    useTranslationText={false}
                    style={styles.studentRecommendationSummaryText}
                  />
                </View>
              ) : null}

              {studentRecommendations.length > 0 && (
                <View style={styles.studentRecommendationListWrap}>
                  <CustomText
                    text={t("Recommendations")}
                    size="xs"
                    weight="bold"
                    useTranslationText={false}
                    style={styles.studentRecommendationListTitle}
                  />
                  <View style={styles.studentRecommendationList}>
                    {studentRecommendations.map((recommendation, idx) => (
                      <View key={idx} style={styles.studentRecommendationItem}>
                        <CustomText
                          text="•"
                          size="xs"
                          useTranslationText={false}
                          style={styles.studentRecommendationBullet}
                        />
                        <CustomText
                          text={recommendation}
                          size="xs"
                          useTranslationText={false}
                          style={styles.studentRecommendationItemText}
                        />
                      </View>
                    ))}
                  </View>
                </View>
              )}
            </View>
          )}

          {student.originality_analysis && (
            <View style={styles.expandedSection}>
              <View style={styles.expandedSectionHeader}>
                <Ionicons name="bulb-outline" size={14} color="#6B7280" />
                <CustomText
                  text={t("Originality Assessment")}
                  size="s"
                  weight="bold"
                  useTranslationText={false}
                  style={styles.expandedSectionTitle}
                />
              </View>
              <CustomText
                text={student.originality_analysis}
                size="xs"
                useTranslationText={false}
                style={styles.expandedSectionText}
              />
            </View>
          )}

          {studentObjectives.length > 0 && (
            <View style={styles.expandedSection}>
              <View style={styles.expandedSectionHeader}>
                <Feather name="target" size={14} color="#6B7280" />
                <CustomText
                  text={t("Learning Objectives ({{count}})", { count: studentObjectives.length })}
                  size="s"
                  weight="bold"
                  useTranslationText={false}
                  style={styles.expandedSectionTitle}
                />
              </View>
              {studentObjectives.map((objective, idx) => {
                const objectiveStatus =
                  objective.status === "completed"
                    ? t("Mastered")
                    : objective.status === "struggling"
                      ? t("Struggling")
                      : t("In Progress");
                const objectiveKey = `${student.student_id}-${objective.objective_id || idx}`;
                const isObjectiveExpanded = expandedStudentObjectiveKey === objectiveKey;

                return (
                  <View key={objectiveKey} style={styles.studentObjectiveCard}>
                    <Pressable
                      onPress={() => setExpandedStudentObjectiveKey(isObjectiveExpanded ? null : objectiveKey)}
                      accessibilityRole="button"
                      accessibilityLabel={
                        isObjectiveExpanded
                          ? t("Collapse objective: {{label}}", { label: objective.objective_label })
                          : t("Expand objective: {{label}}", { label: objective.objective_label })
                      }
                      accessibilityState={{ expanded: isObjectiveExpanded }}
                      style={({ pressed }) => [
                        styles.studentObjectiveButton,
                        pressed && styles.studentObjectiveButtonPressed,
                      ]}>
                      <View style={styles.overviewObjectiveHeaderRow}>
                        <View style={styles.overviewObjectiveHeaderContent}>
                          <CustomText
                            text={`${idx + 1}. ${objective.objective_label}`}
                            size="s"
                            useTranslationText={false}
                            style={styles.overviewObjectiveLabel}
                          />
                          <View style={styles.overviewObjectiveMetaRow}>
                            <CustomText
                              text={objectiveStatus}
                              size="xs"
                              weight="bold"
                              useTranslationText={false}
                              style={
                                objective.status === "completed"
                                  ? styles.objectiveMasteredText
                                  : objective.status === "struggling"
                                    ? styles.objectiveStrugglingText
                                    : styles.objectiveInProgressText
                              }
                            />
                            <View style={styles.metaItem}>
                              <Feather name="message-square" size={12} color="#6B7280" />
                              <CustomText
                                text={`${objective.questions_asked ?? 0} qs`}
                                size="xs"
                                useTranslationText={false}
                                style={styles.metaText}
                              />
                            </View>
                            <View style={styles.metaItem}>
                              <Feather name="clock" size={12} color="#6B7280" />
                              <CustomText
                                text={formatObjectiveTime(objective.total_time_taken)}
                                size="xs"
                                useTranslationText={false}
                                style={styles.metaText}
                              />
                            </View>
                          </View>
                        </View>
                        <Feather name={isObjectiveExpanded ? "chevron-up" : "chevron-down"} size={14} color="#9CA3AF" />
                      </View>
                    </Pressable>

                    {isObjectiveExpanded && (
                      <View style={styles.overviewObjectiveExpandedBody}>
                        {analytics?.summaries?.[0] && (
                          <View style={styles.overviewSummaryBox}>
                            <View style={styles.overviewSummaryRow}>
                              <Ionicons name="sparkles-outline" size={14} color="#7C3AED" />
                              <CustomText
                                text={analytics.summaries[0]}
                                size="xs"
                                useTranslationText={false}
                                style={styles.overviewSummaryText}
                              />
                            </View>
                          </View>
                        )}

                        {(analytics?.success_points_details ?? []).length > 0 && (
                          <View style={styles.overviewSuccessBox}>
                            <View style={styles.overviewSectionHeadingRow}>
                              <Feather name="check-circle" size={14} color="#059669" />
                              <CustomText
                                text={t("What Went Well")}
                                size="s"
                                weight="bold"
                                useTranslationText={false}
                                style={styles.overviewSectionHeadingText}
                              />
                            </View>
                            <View style={styles.overviewBulletList}>
                              {(analytics?.success_points_details ?? []).map((point, pointIdx) => (
                                <View key={pointIdx} style={styles.overviewBulletItem}>
                                  <CustomText
                                    text="•"
                                    size="xs"
                                    useTranslationText={false}
                                    style={styles.overviewBullet}
                                  />
                                  <CustomText
                                    text={point}
                                    size="xs"
                                    useTranslationText={false}
                                    style={styles.overviewBulletText}
                                  />
                                </View>
                              ))}
                            </View>
                          </View>
                        )}
                      </View>
                    )}
                  </View>
                );
              })}
            </View>
          )}

          {(analytics?.success_points_details ?? []).length > 0 && (
            <View style={styles.expandedSection}>
              <View style={styles.expandedSectionHeader}>
                <Feather name="check-circle" size={14} color="#059669" />
                <CustomText
                  text={t("What Went Well")}
                  size="s"
                  weight="bold"
                  useTranslationText={false}
                  style={[styles.expandedSectionTitle, { color: "#059669" }]}
                />
              </View>
              <View style={styles.bulletList}>
                {(analytics?.success_points_details ?? []).map((point, idx) => (
                  <View key={idx} style={styles.expandedBulletItem}>
                    <View style={[styles.bulletPoint, { backgroundColor: "#065F46" }]} />
                    <CustomText text={point} size="xs" useTranslationText={false} style={styles.expandedBulletText} />
                  </View>
                ))}
              </View>
            </View>
          )}
        </View>
      )}
    </View>
  );
};

function LTIAnalyticsView() {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const route = useRoute<RouteProp<RootStackParamList, "LTI Analytics">>();
  const { activityId, accessToken: routeAccessToken, refreshToken: routeRefreshToken } = route.params ?? {};
  const { login, isAuthenticated, didCheckToken, userInfo } = useAuth();
  const { t } = useTranslation();
  const canEdit = hasMinRole(userInfo.userRole, UserRoleUI.FACULTY);
  const [authReady, setAuthReady] = useState(false);
  const [activeTab, setActiveTab] = useState<LtiAnalyticsTab>("overview");
  const [expandedStudentId, setExpandedStudentId] = useState<string | null>(null);
  const [expandedOverviewObjectiveId, setExpandedOverviewObjectiveId] = useState<string | null>(null);
  const [expandedStudentObjectiveKey, setExpandedStudentObjectiveKey] = useState<string | null>(null);

  useFocusEffect(
    useCallback(() => {
      if (!routeAccessToken || !routeRefreshToken) return;

      let isActive = true;

      const bootstrapAuth = async () => {
        try {
          await login(routeAccessToken, routeRefreshToken);
        } catch (error) {
          Sentry.captureException(error);
          if (!isActive) return;
          navigation.reset({
            index: 0,
            routes: [{ name: "LTIErrorView", params: { error: "auth_failed" } }],
          });
          return;
        }
        if (!isActive) return;
        setAuthReady(true);
      };

      void bootstrapAuth();

      return () => {
        isActive = false;
      };
    }, [login, navigation, routeAccessToken, routeRefreshToken])
  );

  useEffect(() => {
    if (routeAccessToken && routeRefreshToken) return;
    if (!didCheckToken) return;
    if (isAuthenticated) {
      setAuthReady(true);
    } else {
      navigation.reset({
        index: 0,
        routes: [{ name: "LTIErrorView", params: { error: "auth_failed" } }],
      });
    }
  }, [didCheckToken, isAuthenticated, navigation, routeAccessToken, routeRefreshToken]);

  useEffect(() => {
    if (!authReady || activityId) return;
    navigation.reset({
      index: 0,
      routes: [{ name: "LTIErrorView", params: { error: "invalid_launch" } }],
    });
  }, [activityId, authReady, navigation]);

  const { data, loading } = useGetAiTutorQuery({
    variables: { id: activityId ?? "" },
    skip: !activityId || !authReady,
  });

  const { data: ltiAnalyticsData, loading: ltiAnalyticsLoading } = useGetAiTutorLtiAnalyticsQuery({
    variables: { aiTutorId: activityId },
    skip: !activityId || !authReady,
  });
  const { data: tenantData } = useGetTenantForSchoolAdminQuery({
    skip: !authReady,
  });

  const analytics = ltiAnalyticsData?.getAITutorLtiAnalytics?.data?.[0];
  const tenant = tenantData?.getTenantForSchoolAdmin?.data?.[0];
  const studentRows = useMemo(() => analytics?.students ?? [], [analytics?.students]);
  const recentActivityByStudentId = useMemo(() => {
    const activities = analytics?.recent_activity ?? [];
    const grouped = new Map<string, (typeof activities)[number][]>();

    activities.forEach((activity) => {
      const key = activity.student_id;
      if (!key) return;

      const existing = grouped.get(key);
      if (existing) {
        existing.push(activity);
        return;
      }

      grouped.set(key, [activity]);
    });

    return grouped;
  }, [analytics?.recent_activity]);
  const overviewObjectives = useMemo(() => {
    const activities = analytics?.recent_activity ?? [];
    const grouped = new Map<string, (typeof activities)[number]>();

    activities.forEach((activity, idx) => {
      const key = activity.objective_id || activity.objective_label || `objective-${idx}`;
      const existing = grouped.get(key);

      if (!existing) {
        grouped.set(key, { ...activity });
        return;
      }

      const mergedStatus =
        existing.status == null
          ? (activity.status ?? existing.status)
          : activity.status == null
            ? existing.status
            : mergeObjectiveStatus(existing.status, activity.status);

      grouped.set(key, {
        ...existing,
        status: mergedStatus,
        questions_asked: (existing.questions_asked ?? 0) + (activity.questions_asked ?? 0),
        total_time_taken: (existing.total_time_taken ?? 0) + (activity.total_time_taken ?? 0),
        success_points_count: (existing.success_points_count ?? 0) + (activity.success_points_count ?? 0),
        confusion_points_count: (existing.confusion_points_count ?? 0) + (activity.confusion_points_count ?? 0),
      });
    });

    return Array.from(grouped.values());
  }, [analytics?.recent_activity]);
  const fetchedTitle = data?.getAITutor?.data?.[0]?.title;
  const fetchedDescription = data?.getAITutor?.data?.[0]?.description;
  const title = fetchedTitle || analytics?.aiTutor_name || "Untitled Activity";
  const description = fetchedDescription || analytics?.aiTutor_description || t("No description available.");
  const createdDate = analytics?.created
    ? new Date(analytics.created).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "-";
  const dueDate = analytics?.dueDate
    ? new Date(analytics.dueDate).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : null;
  const context = analytics?.context?.trim() || null;
  const shouldShowContext = Boolean(context) && tenant?.ltiActivitiesEnabled === false;

  const completedCount = analytics
    ? Math.round(((analytics.completion_rate ?? 0) / 100) * (analytics.enrollment ?? 0))
    : 0;

  const confusionRate =
    analytics && (analytics.success_points ?? 0) + (analytics.confusion_points ?? 0) > 0
      ? Math.round(
          ((analytics.confusion_points ?? 0) / ((analytics.success_points ?? 0) + (analytics.confusion_points ?? 0))) *
            100
        )
      : 0;

  const sentimentCounts = useMemo(() => {
    return studentRows.reduce(
      (acc, student) => {
        if (student.sentiment === "positive") acc.positive += 1;
        if (student.sentiment === "neutral") acc.neutral += 1;
        if (student.sentiment === "negative") acc.negative += 1;
        return acc;
      },
      { positive: 0, neutral: 0, negative: 0 }
    );
  }, [studentRows]);

  const totalSentimentCount = sentimentCounts.positive + sentimentCounts.neutral + sentimentCounts.negative;
  const sentimentPositiveFlex = totalSentimentCount > 0 ? sentimentCounts.positive : 0;
  const sentimentNeutralFlex = totalSentimentCount > 0 ? sentimentCounts.neutral : 0;
  const sentimentNegativeFlex = totalSentimentCount > 0 ? sentimentCounts.negative : 0;

  return (
    <LTILayout>
      {!authReady || loading || ltiAnalyticsLoading ? (
        <View style={styles.loadingContainer}>
          <CustomText text={t("Loading...")} size="m" useTranslationText={false} style={styles.loadingText} />
        </View>
      ) : (
        <View style={styles.container}>
          <View style={styles.dashboard}>
            {/* Main Title Card */}
            <View style={styles.card}>
              <View style={styles.titleRow}>
                <CustomText text={title} size="xl" weight="bold" useTranslationText={false} style={styles.titleText} />
                <View style={styles.activeBadge}>
                  <CustomText text={t("Active")} size="xs" useTranslationText={false} style={styles.activeBadgeText} />
                </View>
              </View>

              <CustomText text={description} size="s" useTranslationText={false} style={styles.description} />

              <View style={styles.metaRow}>
                <View style={styles.metaItem}>
                  <Feather name="calendar" size={12} color="#6B7280" />
                  <CustomText text={createdDate} size="xs" useTranslationText={false} style={styles.metaText} />
                </View>
                {dueDate && (
                  <View style={styles.metaItem}>
                    <Feather name="calendar" size={12} color="#6B7280" />
                    <CustomText
                      text={t("Due {{date}}", { date: dueDate })}
                      size="xs"
                      useTranslationText={false}
                      style={styles.metaText}
                    />
                  </View>
                )}
                {shouldShowContext && (
                  <CustomText text={context} size="xs" useTranslationText={false} style={styles.metaText} />
                )}
              </View>

              <View style={styles.metricsRow}>
                <View style={styles.metaItem}>
                  <Feather name="clock" size={12} color="#6B7280" />
                  <CustomText
                    text={formatTime(analytics?.average_time_taken ?? 0)}
                    size="xs"
                    useTranslationText={false}
                    style={styles.metricText}
                  />
                </View>
                <CustomText
                  text={`${analytics?.completion_rate ?? 0}% completion`}
                  size="xs"
                  useTranslationText={false}
                  style={styles.metricText}
                />
                <CustomText
                  text={`Avg progress: ${(analytics?.average_progress ?? 0).toFixed(1)}%`}
                  size="xs"
                  useTranslationText={false}
                  style={styles.metricText}
                />
                <View style={styles.metaItem}>
                  <Feather name="check-circle" size={12} color="#6B7280" />
                  <CustomText
                    text={` ${t("Success points")} : ${analytics?.success_points ?? 0}`}
                    size="xs"
                    useTranslationText={false}
                    style={styles.metricText}
                  />
                </View>
                <View style={styles.metaItem}>
                  <Feather name="alert-circle" size={12} color="#6B7280" />
                  <CustomText
                    text={` ${t("Confusion points")} : ${analytics?.confusion_points ?? 0}`}
                    size="xs"
                    useTranslationText={false}
                    style={styles.metricText}
                  />
                </View>
              </View>

              <View style={styles.actionsRow}>
                <View style={styles.iconButtons}>
                  {activityId && canEdit ? (
                    <Pressable
                      onPress={() => navigation.navigate("LTI Edit ILE", { activityId })}
                      accessibilityLabel="Edit ILE"
                      accessibilityRole="button">
                      <Feather name="edit-2" size={16} color="#9CA3AF" />
                    </Pressable>
                  ) : (
                    <Feather name="edit-2" size={16} color="#D1D5DB" />
                  )}
                </View>
              </View>
            </View>

            {/* Tabs */}
            <View style={styles.tabsRow}>
              <Pressable
                style={[styles.tabItem, activeTab === "overview" && styles.tabActive]}
                onPress={() => setActiveTab("overview")}
                accessibilityRole="tab"
                accessibilityState={{ selected: activeTab === "overview" }}
                accessibilityLabel={t("Overview analytics tab")}>
                <CustomText
                  text={t("Overview")}
                  size="s"
                  weight="medium"
                  useTranslationText={false}
                  style={activeTab === "overview" ? styles.tabActiveText : styles.tabInactiveText}
                />
              </Pressable>
              <Pressable
                style={[styles.tabItem, activeTab === "students" && styles.tabActive]}
                onPress={() => setActiveTab("students")}
                accessibilityRole="tab"
                accessibilityState={{ selected: activeTab === "students" }}
                accessibilityLabel={t("Students analytics tab")}>
                <CustomText
                  text={t("Students")}
                  size="s"
                  weight="medium"
                  useTranslationText={false}
                  style={activeTab === "students" ? styles.tabActiveText : styles.tabInactiveText}
                />
                <View style={[styles.badgeNum, activeTab === "students" && styles.badgeNumActive]}>
                  <CustomText
                    text={String(analytics?.student_count ?? 0)}
                    size="xs"
                    useTranslationText={false}
                    style={activeTab === "students" ? styles.badgeNumActiveText : styles.badgeNumText}
                  />
                </View>
              </Pressable>
            </View>

            {activeTab === "overview" ? (
              <>
                {/* 4 KPI Cards */}
                <View style={styles.kpiGrid}>
                  <View style={styles.kpiCard}>
                    <View style={styles.kpiHeader}>
                      <Feather name="users" size={14} color="#6B7280" />
                      <CustomText text={t("Enrolled")} size="s" useTranslationText={false} style={styles.kpiLabel} />
                    </View>
                    <CustomText
                      text={String(analytics?.enrollment ?? 0)}
                      size="xxl"
                      weight="bold"
                      useTranslationText={false}
                      style={styles.kpiValue}
                    />
                    <CustomText
                      text={t("{{count}} completed", { count: completedCount })}
                      size="xs"
                      useTranslationText={false}
                      style={styles.kpiSubValue}
                    />
                  </View>
                  <View style={styles.kpiCard}>
                    <View style={styles.kpiHeader}>
                      <Feather name="trending-up" size={14} color="#10B981" />
                      <CustomText
                        text={t("Completion Rate")}
                        size="s"
                        useTranslationText={false}
                        style={styles.kpiLabel}
                      />
                    </View>
                    <CustomText
                      text={`${analytics?.completion_rate ?? 0}%`}
                      size="xxl"
                      weight="bold"
                      useTranslationText={false}
                      style={styles.kpiValue}
                    />
                  </View>
                  <View style={styles.kpiCard}>
                    <View style={styles.kpiHeader}>
                      <Feather name="clock" size={14} color="#3B82F6" />
                      <CustomText
                        text={t("Avg Time Spent")}
                        size="s"
                        useTranslationText={false}
                        style={styles.kpiLabel}
                      />
                    </View>
                    <CustomText
                      text={formatTime(analytics?.average_time_taken ?? 0)}
                      size="xxl"
                      weight="bold"
                      useTranslationText={false}
                      style={styles.kpiValue}
                    />
                  </View>
                  <View style={styles.kpiCard}>
                    <View style={styles.kpiHeader}>
                      <Feather name="info" size={14} color="#EF4444" />
                      <CustomText
                        text={t("Confusion Rate")}
                        size="s"
                        useTranslationText={false}
                        style={styles.kpiLabel}
                      />
                    </View>
                    <CustomText
                      text={`${confusionRate}%`}
                      size="xxl"
                      weight="bold"
                      useTranslationText={false}
                      style={styles.kpiValue}
                    />
                  </View>
                </View>

                {/* Overall Sentiment */}
                <View style={styles.card}>
                  <CustomText
                    text={t("Overall Sentiment")}
                    size="s"
                    weight="bold"
                    useTranslationText={false}
                    style={styles.cardTitle}
                  />
                  <View style={styles.sentimentBarContainer}>
                    {sentimentPositiveFlex > 0 && (
                      <View
                        style={[
                          styles.sentimentSegment,
                          {
                            flex: sentimentPositiveFlex,
                            backgroundColor: "#10B981",
                            borderTopLeftRadius: 4,
                            borderBottomLeftRadius: 4,
                            borderTopRightRadius: sentimentNeutralFlex === 0 && sentimentNegativeFlex === 0 ? 4 : 0,
                            borderBottomRightRadius: sentimentNeutralFlex === 0 && sentimentNegativeFlex === 0 ? 4 : 0,
                          },
                        ]}
                      />
                    )}
                    {sentimentNeutralFlex > 0 && (
                      <View
                        style={[
                          styles.sentimentSegment,
                          {
                            flex: sentimentNeutralFlex,
                            backgroundColor: "#F59E0B",
                            borderTopLeftRadius: sentimentPositiveFlex === 0 ? 4 : 0,
                            borderBottomLeftRadius: sentimentPositiveFlex === 0 ? 4 : 0,
                            borderTopRightRadius: sentimentNegativeFlex === 0 ? 4 : 0,
                            borderBottomRightRadius: sentimentNegativeFlex === 0 ? 4 : 0,
                          },
                        ]}
                      />
                    )}
                    {sentimentNegativeFlex > 0 && (
                      <View
                        style={[
                          styles.sentimentSegment,
                          {
                            flex: sentimentNegativeFlex,
                            backgroundColor: "#EF4444",
                            borderTopRightRadius: 4,
                            borderBottomRightRadius: 4,
                            borderTopLeftRadius: sentimentPositiveFlex === 0 && sentimentNeutralFlex === 0 ? 4 : 0,
                            borderBottomLeftRadius: sentimentPositiveFlex === 0 && sentimentNeutralFlex === 0 ? 4 : 0,
                          },
                        ]}
                      />
                    )}
                  </View>
                  <View style={styles.legendRow}>
                    <View style={styles.legendItem}>
                      <View style={[styles.dot, { backgroundColor: "#10B981" }]} />
                      <CustomText
                        text={t("Positive ({{count}})", { count: sentimentCounts.positive })}
                        size="xs"
                        useTranslationText={false}
                        style={styles.legendText}
                      />
                    </View>
                    <View style={styles.legendItem}>
                      <View style={[styles.dot, { backgroundColor: "#F59E0B" }]} />
                      <CustomText
                        text={t("Neutral ({{count}})", { count: sentimentCounts.neutral })}
                        size="xs"
                        useTranslationText={false}
                        style={styles.legendText}
                      />
                    </View>
                    <View style={styles.legendItem}>
                      <View style={[styles.dot, { backgroundColor: "#EF4444" }]} />
                      <CustomText
                        text={t("Negative ({{count}})", { count: sentimentCounts.negative })}
                        size="xs"
                        useTranslationText={false}
                        style={styles.legendText}
                      />
                    </View>
                  </View>
                </View>

                {/* Recommendations Spotlight */}
                {(analytics?.summaries?.length ?? 0) > 0 || (analytics?.further_recommendations?.length ?? 0) > 0 ? (
                  <View style={styles.recommendationCard}>
                    {(analytics?.summaries?.length ?? 0) > 0 && (
                      <View style={styles.recommendationSummaryRow}>
                        <Ionicons name="sparkles-outline" size={14} color="#7C3AED" />
                        <CustomText
                          text={analytics?.summaries?.[0] ?? ""}
                          size="xs"
                          useTranslationText={false}
                          style={styles.recommendationSummaryText}
                        />
                      </View>
                    )}

                    {(analytics?.further_recommendations?.length ?? 0) > 0 && (
                      <View style={styles.recommendationListWrap}>
                        <CustomText
                          text={t("Recommendations")}
                          size="xs"
                          weight="bold"
                          useTranslationText={false}
                          style={styles.recTitle}
                        />
                        <View style={styles.bulletList}>
                          {analytics?.further_recommendations?.map((rec, idx) => (
                            <View key={idx} style={styles.bulletItem}>
                              <View style={styles.bulletPoint} />
                              <CustomText text={rec} size="xs" useTranslationText={false} style={styles.recText} />
                            </View>
                          ))}
                        </View>
                      </View>
                    )}
                  </View>
                ) : null}

                {/* Time summary */}
                <View
                  style={[styles.card, { flexDirection: "row", alignItems: "center", gap: 24, paddingVertical: 16 }]}>
                  <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
                    <CustomText text={t("Total Time")} size="xs" useTranslationText={false} style={styles.metaText} />
                    <CustomText
                      text={formatTime(analytics?.totalTimeTaken ?? 0)}
                      size="s"
                      weight="bold"
                      useTranslationText={false}
                      style={{ color: "#111827" }}
                    />
                  </View>
                  <View style={{ width: 1, height: 16, backgroundColor: "#E5E7EB" }} />
                  <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
                    <CustomText
                      text={t("Questions Asked")}
                      size="xs"
                      useTranslationText={false}
                      style={styles.metaText}
                    />
                    <CustomText
                      text={String(analytics?.questions_asked ?? 0)}
                      size="s"
                      weight="bold"
                      useTranslationText={false}
                      style={{ color: "#111827" }}
                    />
                  </View>
                  <View style={{ width: 1, height: 16, backgroundColor: "#E5E7EB" }} />
                  <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
                    <CustomText
                      text={t("Active Interactions")}
                      size="xs"
                      useTranslationText={false}
                      style={styles.metaText}
                    />
                    <CustomText
                      text={String(analytics?.active_interaction_count ?? 0)}
                      size="s"
                      weight="bold"
                      useTranslationText={false}
                      style={{ color: "#111827" }}
                    />
                  </View>
                  <View style={{ width: 1, height: 16, backgroundColor: "#E5E7EB" }} />
                  <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
                    <CustomText text={t("AI Response")} size="xs" useTranslationText={false} style={styles.metaText} />
                    <CustomText
                      text={(() => {
                        const vals = (analytics?.students ?? [])
                          .map((s) => s.average_ai_response_time)
                          .filter((v): v is number => v != null);
                        if (vals.length === 0) return "—";
                        return formatDurationFromSeconds(vals.reduce((sum, v) => sum + v, 0) / vals.length);
                      })()}
                      size="s"
                      weight="bold"
                      useTranslationText={false}
                      style={{ color: "#111827" }}
                    />
                  </View>
                </View>

                {/* Learning Objectives */}
                {overviewObjectives.length > 0 && (
                  <View style={styles.card}>
                    <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 12 }}>
                      <Feather name="target" size={14} color="#6B7280" />
                      <CustomText
                        text={t("Learning Objectives ({{count}})", { count: overviewObjectives.length })}
                        size="s"
                        weight="bold"
                        useTranslationText={false}
                        style={{ marginLeft: 6, color: "#374151" }}
                      />
                    </View>

                    {overviewObjectives.map((activity, idx) => {
                      const objectiveKey = activity.objective_id || `${activity.objective_label}-${idx}`;

                      return (
                        <OverviewObjectiveCard
                          key={objectiveKey}
                          activity={activity}
                          idx={idx}
                          isLast={idx === overviewObjectives.length - 1}
                          isExpanded={expandedOverviewObjectiveId === objectiveKey}
                          analytics={analytics}
                          setExpandedOverviewObjectiveId={setExpandedOverviewObjectiveId}
                          t={t}
                        />
                      );
                    })}
                  </View>
                )}
              </>
            ) : (
              <View style={styles.studentsTable}>
                <View style={styles.studentsHeaderRow}>
                  <CustomText
                    text={t("Student")}
                    size="xs"
                    useTranslationText={false}
                    style={[styles.studentsHeaderText, styles.studentColumn]}
                  />
                  <CustomText
                    text={t("Status")}
                    size="xs"
                    useTranslationText={false}
                    style={[styles.studentsHeaderText, styles.statusColumn]}
                  />
                  <CustomText
                    text={t("Time Spent")}
                    size="xs"
                    useTranslationText={false}
                    style={[styles.studentsHeaderText, styles.timeColumn]}
                  />
                  <CustomText
                    text={t("Confusion")}
                    size="xs"
                    useTranslationText={false}
                    style={[styles.studentsHeaderText, styles.confusionColumn]}
                  />
                  <CustomText
                    text={t("Sentiment")}
                    size="xs"
                    useTranslationText={false}
                    style={[styles.studentsHeaderText, styles.sentimentColumn]}
                  />
                  <CustomText
                    text={t("Originality")}
                    size="xs"
                    useTranslationText={false}
                    style={[styles.studentsHeaderText, styles.originalityColumn]}
                  />
                  <View style={styles.studentsChevronColumn} />
                </View>
                {studentRows.length > 0 ? (
                  studentRows.map((student) => (
                    <StudentAnalyticsRow
                      key={student.student_id}
                      student={student}
                      analytics={analytics}
                      isExpanded={expandedStudentId === student.student_id}
                      studentObjectives={recentActivityByStudentId.get(student.student_id) ?? []}
                      expandedStudentObjectiveKey={expandedStudentObjectiveKey}
                      setExpandedStudentId={setExpandedStudentId}
                      setExpandedStudentObjectiveKey={setExpandedStudentObjectiveKey}
                      t={t}
                    />
                  ))
                ) : (
                  <View style={styles.studentsEmptyState}>
                    <CustomText
                      text={t("No student analytics yet.")}
                      size="s"
                      useTranslationText={false}
                      style={styles.studentEmailText}
                    />
                  </View>
                )}
              </View>
            )}
            <CustomSpacing type="vertical" size="xxl" />
          </View>
        </View>
      )}
    </LTILayout>
  );
}

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 48,
  },
  loadingText: {
    color: "#6B7280",
  },
  container: {
    backgroundColor: "#F9FAFB",
    padding: 24,
    width: "100%",
  },
  dashboard: {
    maxWidth: 1000,
    width: "100%",
    alignSelf: "center",
    gap: 16,
  },
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    padding: 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.03,
    shadowRadius: 2,
    elevation: 1,
  },
  titleRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  titleText: {
    color: "#111827",
    marginRight: 12,
  },
  activeBadge: {
    borderColor: "#10B981",
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  activeBadgeText: {
    color: "#059669",
  },
  description: {
    color: "#4B5563",
    lineHeight: 20,
    marginBottom: 12,
  },
  metaRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 12,
  },
  metaItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  metaText: {
    color: "#6B7280",
  },
  metricsRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 16,
    marginBottom: 24,
  },
  metricText: {
    color: "#374151",
    fontWeight: "500",
  },
  actionsRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  iconButtons: {
    flexDirection: "row",
    gap: 16,
  },
  tabsRow: {
    flexDirection: "row",
    gap: 8,
    marginBottom: 8,
  },
  tabItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
  },
  tabActive: {
    backgroundColor: "#EFF6FF",
  },
  tabActiveText: {
    color: "#2563EB",
  },
  tabInactiveText: {
    color: "#6B7280",
  },
  badgeNum: {
    backgroundColor: "#F3F4F6",
    borderRadius: 10,
    paddingHorizontal: 6,
    marginLeft: 6,
  },
  badgeNumText: {
    color: "#4B5563",
  },
  kpiGrid: {
    flexDirection: "row",
    gap: 16,
    flexWrap: "wrap",
  },
  kpiCard: {
    flex: 1,
    minWidth: 150,
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    padding: 16,
  },
  kpiHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginBottom: 12,
  },
  kpiLabel: {
    color: "#4B5563",
  },
  kpiValue: {
    color: "#111827",
  },
  kpiSubValue: {
    color: "#6B7280",
    marginTop: 4,
  },
  cardTitle: {
    color: "#111827",
    marginBottom: 12,
  },
  sentimentBarContainer: {
    height: 12,
    flexDirection: "row",
    marginBottom: 12,
  },
  sentimentSegment: {
    height: "100%",
  },
  legendRow: {
    flexDirection: "row",
    gap: 16,
  },
  legendItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  legendText: {
    color: "#6B7280",
  },
  recommendationCard: {
    backgroundColor: "#EFF6FF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#DBEAFE",
    padding: 20,
    gap: 10,
  },
  recommendationSummaryRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
  },
  recommendationSummaryText: {
    color: "#312E81",
    lineHeight: 20,
    flex: 1,
  },
  recommendationListWrap: {
    paddingLeft: 22,
  },
  recTitle: {
    color: "#4338CA",
    marginBottom: 8,
  },
  recText: {
    color: "#312E81",
    lineHeight: 20,
  },
  bulletList: {
    marginLeft: 4,
    gap: 6,
  },
  bulletItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
  },
  bulletPoint: {
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: "#4338CA",
    marginTop: 8,
  },
  overviewObjectiveCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    marginBottom: 8,
    overflow: "hidden",
  },
  overviewObjectiveCardLast: {
    marginBottom: 0,
  },
  overviewObjectiveButton: {
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  overviewObjectiveButtonPressed: {
    backgroundColor: "#F9FAFB",
  },
  overviewObjectiveHeaderRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 8,
  },
  overviewObjectiveHeaderContent: {
    flex: 1,
  },
  overviewObjectiveLabel: {
    color: "#111827",
    lineHeight: 20,
  },
  overviewObjectiveMetaRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
    marginTop: 6,
  },
  overviewObjectiveExpandedBody: {
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
    paddingHorizontal: 12,
    paddingVertical: 12,
    gap: 12,
  },
  overviewSummaryBox: {
    borderLeftWidth: 2,
    borderLeftColor: "#7C3AED",
    paddingLeft: 10,
  },
  overviewSummaryRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
  },
  overviewSummaryText: {
    color: "#4B5563",
    lineHeight: 18,
    flex: 1,
  },
  overviewSuccessBox: {
    borderLeftWidth: 2,
    borderLeftColor: "#059669",
    paddingLeft: 10,
  },
  overviewConfusionBox: {
    borderLeftWidth: 2,
    borderLeftColor: "#DC2626",
    paddingLeft: 10,
  },
  overviewSectionHeadingRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginBottom: 6,
  },
  overviewSectionHeadingText: {
    color: "#111827",
  },
  overviewBulletList: {
    gap: 4,
  },
  overviewBulletItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 6,
  },
  overviewBullet: {
    color: "#9CA3AF",
    marginTop: 1,
  },
  overviewBulletText: {
    color: "#4B5563",
    lineHeight: 17,
    flex: 1,
  },
  studentObjectiveCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    marginBottom: 8,
    overflow: "hidden",
  },
  studentObjectiveButton: {
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  studentObjectiveButtonPressed: {
    backgroundColor: "#F9FAFB",
  },
  // Students Table Styles
  studentsTable: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.03,
    shadowRadius: 2,
    elevation: 1,
    overflow: "hidden",
  },
  studentsHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: "#F9FAFB",
  },
  studentsHeaderText: {
    color: "#6B7280",
    fontWeight: "600",
  },
  studentsBodyRow: {
    flexDirection: "row",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#F3F4F6",
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 12,
  },
  studentColumn: {
    flex: 3,
    minWidth: 180,
  },
  statusColumn: {
    flex: 1.5,
    minWidth: 100,
  },
  timeColumn: {
    flex: 1,
    minWidth: 80,
  },
  confusionColumn: {
    flex: 0.8,
    minWidth: 70,
    textAlign: "center",
  },
  sentimentColumn: {
    flex: 1.2,
    minWidth: 90,
  },
  originalityColumn: {
    flex: 2,
    minWidth: 150,
  },
  studentsChevronColumn: {
    width: 24,
    alignItems: "center",
  },
  studentIdentityCell: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  studentAvatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: "#DBEAFE",
    alignItems: "center",
    justifyContent: "center",
  },
  studentAvatarText: {
    color: "#0369A1",
  },
  studentNameBlock: {
    flex: 1,
  },
  studentNameText: {
    color: "#111827",
    fontWeight: "500",
  },
  studentEmailText: {
    color: "#9CA3AF",
    marginTop: 2,
  },
  statusPill: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    paddingHorizontal: 10,
    paddingVertical: 2,
    borderRadius: 999,
    borderWidth: 1,
  },
  statusPillComplete: {
    backgroundColor: "#D1FAE5",
    borderColor: "#059669",
  },
  statusPillCompleteText: {
    color: "#059669",
    fontWeight: "600",
  },
  statusPillProgress: {
    backgroundColor: "#FFFBEB",
    borderColor: "#F59E0B",
  },
  statusPillProgressText: {
    color: "#D97706",
    fontWeight: "600",
  },
  statusPillNotStarted: {
    backgroundColor: "#F3F4F6",
    borderColor: "#D1D5DB",
  },
  statusPillNotStartedText: {
    color: "#6B7280",
    fontWeight: "600",
  },
  studentsCellText: {
    color: "#374151",
  },
  sentimentPill: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    paddingHorizontal: 10,
    paddingVertical: 2,
    borderRadius: 999,
    borderWidth: 1,
  },
  sentimentPillPositive: {
    backgroundColor: "#D1FAE5",
    borderColor: "#059669",
  },
  sentimentPillPositiveText: {
    color: "#059669",
    fontWeight: "600",
  },
  sentimentPillNeutral: {
    backgroundColor: "#F3F4F6",
    borderColor: "#D1D5DB",
  },
  sentimentPillNeutralText: {
    color: "#6B7280",
    fontWeight: "600",
  },
  sentimentPillNegative: {
    backgroundColor: "#FEF2F2",
    borderColor: "#EF4444",
  },
  sentimentPillNegativeText: {
    color: "#DC2626",
    fontWeight: "600",
  },
  originalityText: {
    color: "#6B7280",
  },
  studentsEmptyState: {
    paddingVertical: 32,
    paddingHorizontal: 16,
    alignItems: "center",
  },
  badgeNumActive: {
    backgroundColor: "#2563EB",
  },
  badgeNumActiveText: {
    color: "#FFFFFF",
  },

  // Expanded Row Detail Styles
  expandedRowDetail: {
    borderBottomWidth: 1,
    borderBottomColor: "#F3F4F6",
    paddingHorizontal: 16,
    paddingVertical: 16,
    backgroundColor: "#FAFBFC",
    gap: 16,
  },
  expandedMetricsRow: {
    flexDirection: "row",
    gap: 12,
    flexWrap: "wrap",
  },
  metricBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    backgroundColor: "#F3F4F6",
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
  },
  expandedMetricText: {
    color: "#374151",
  },
  expandedTimingStrip: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 0,
    backgroundColor: "#FFFFFF",
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  expandedTimingItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 8,
  },
  expandedTimingDivider: {
    width: 1,
    height: 16,
    backgroundColor: "#E5E7EB",
  },
  expandedTimingLabel: {
    color: "#6B7280",
  },
  expandedTimingValue: {
    color: "#111827",
  },
  studentRecommendationCard: {
    backgroundColor: "#F5F3FF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#DDD6FE",
    padding: 12,
    gap: 10,
  },
  studentRecommendationHeaderRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
  },
  studentRecommendationSummaryText: {
    color: "#312E81",
    lineHeight: 18,
    flex: 1,
  },
  studentRecommendationListWrap: {
    paddingLeft: 22,
    gap: 6,
  },
  studentRecommendationListTitle: {
    color: "#6D28D9",
  },
  studentRecommendationList: {
    gap: 4,
  },
  studentRecommendationItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 6,
  },
  studentRecommendationBullet: {
    color: "#7C3AED",
    marginTop: 1,
  },
  studentRecommendationItemText: {
    color: "#4B5563",
    lineHeight: 17,
    flex: 1,
  },
  expandedSection: {
    backgroundColor: "#FFFFFF",
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    padding: 12,
  },
  expandedSectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 10,
  },
  expandedSectionTitle: {
    color: "#111827",
  },
  expandedSectionText: {
    color: "#4B5563",
    lineHeight: 18,
  },
  objectiveMasteredText: {
    color: "#059669",
  },
  objectiveInProgressText: {
    color: "#D97706",
  },
  objectiveStrugglingText: {
    color: "#DC2626",
  },
  expandedBulletItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
    marginBottom: 8,
  },
  expandedBulletText: {
    color: "#4B5563",
    lineHeight: 16,
  },
  chevronRotated: {
    transform: [{ rotate: "180deg" }],
  },
});

export default LTIAnalyticsView;
